/**
 * KB-3271 — the "we have seen a live apex session for this uid" flag (AC-1).
 *
 * Pro cannot observe the apex (`kidsbooked.com`) `__session` cookie directly —
 * it is `HttpOnly` and scoped to another origin. So `apexSessionChecker.ts`
 * detects "the apex session went away" by polling `custom-token`. But a naive
 * "no apex session ⇒ sign out of pro" is wrong: a director who signs into pro
 * DIRECTLY has no apex session at that instant, and KB-3235's fire-and-forget
 * mint can fail silently — so that director would be signed straight back out.
 *
 * This flag is the guard that distinguishes **"there was never an apex session"**
 * from **"there was one and it went away."** The checker does NOTHING unless this
 * flag is set for the current uid (AC-1: absence of the flag means the checker is
 * a no-op, even on a definitive negative from the endpoint).
 *
 * ## Shape: one key holding the observed uid
 *
 * Stored as a single key whose VALUE is the uid an apex session was last observed
 * for, rather than a per-uid key, so it self-scopes on user-switch (a new
 * observation overwrites) and never accumulates stale keys. `hasApexSessionObserved`
 * is therefore "the stored uid IS the current uid" — a flag set for user A cannot
 * arm the checker for user B.
 *
 * ## Set on OBSERVE, cleared on SIGN-OUT
 *
 * Set at the two places pro sees a live apex session: `apexSsoBootstrap` resolving
 * `allowed` (adopted one) and `mintApexSession` resolving `minted` (created one).
 * Both are recorded independently — a successful adopt means we saw a real apex
 * session even if the follow-on mint later fails. Cleared at the `AuthContext.signOut`
 * chokepoint (so KB-3270's federated path clears it for free) and by the checker's
 * own apex-ended sign-out.
 *
 * Dependency-free on purpose (safe to import at module scope from the mockable
 * behaviour modules `apexSessionMint` / `apexSsoBootstrap` — landmine L379). Every
 * access is wrapped: `localStorage` throws in Safari private mode and on quota, and
 * is absent in non-browser test contexts. A storage failure resolves to
 * "not observed" (`hasApexSessionObserved` → false), which fails toward NOT signing
 * anyone out — the same fail-open direction AC-2 demands of the checker itself.
 */
const APEX_OBSERVED_UID_KEY = "kb_apex_session_observed_uid";

/** Record that a live apex session has been seen for `uid` (AC-1 set). */
export function recordApexSessionObserved(uid: string): void {
  if (!uid) return;
  try {
    localStorage.setItem(APEX_OBSERVED_UID_KEY, uid);
  } catch (err) {
    // Safari private mode / quota / disabled storage. The checker degrades to a
    // no-op (hasApexSessionObserved → false), which is the safe direction.
    console.warn("apexObservedState: could not record observed apex session:", err);
  }
}

/**
 * Clear the observed-apex flag for `uid` (AC-1 clear). Scoped to `uid` so an
 * eject/refusal sign-out for a caller who never had an apex session cannot wipe a
 * different, live user's flag in a race.
 */
export function clearApexSessionObserved(uid: string): void {
  if (!uid) return;
  try {
    if (localStorage.getItem(APEX_OBSERVED_UID_KEY) === uid) {
      localStorage.removeItem(APEX_OBSERVED_UID_KEY);
    }
  } catch (err) {
    console.warn("apexObservedState: could not clear observed apex session:", err);
  }
}

/**
 * Whether a live apex session has been observed for `uid` and not since cleared.
 * Returns false on any storage error — the checker must fail toward not signing
 * anyone out.
 */
export function hasApexSessionObserved(uid: string): boolean {
  if (!uid) return false;
  try {
    return localStorage.getItem(APEX_OBSERVED_UID_KEY) === uid;
  } catch {
    return false;
  }
}
